<script setup lang="ts" generic="T extends Record<string, any>">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import DropdownOptionItem from "./DropdownOptionItem.vue";
import IconManager from "@/core/IconManager.vue";

type SelectedValue<T> = T | { option: T; subOption: any };

const props = withDefaults(
  defineProps<{
    title?: string;
    data: T[];
    dataKey: keyof T;
    dataValue: keyof T;
    subOptionsKey?: string;
    subOptionKey?: string;
    subOptionValue?: string;
    placeholder?: string;
    modelValue?: SelectedValue<T> | null;
    readonly?: boolean;
  }>(),
  {
    subOptionsKey: "subOptions",
    readonly: false,
    modelValue: null,
  },
);

const { t } = useI18n();

const emit = defineEmits<{
  "update:modelValue": [value: SelectedValue<T>];
}>();

const isOpen = ref(false);
const rootEl = ref<HTMLElement | null>(null);

function toggleOpen() {
  if (props.readonly) return;
  isOpen.value = !isOpen.value;
}

function select(item: any, parent?: T) {
  const value: SelectedValue<T> = parent
    ? { option: parent, subOption: item }
    : item;
  emit("update:modelValue", value);
  isOpen.value = false;
}

function isGrouped(
  value: SelectedValue<T>,
): value is { option: T; subOption: any } {
  return (
    value !== null &&
    typeof value === "object" &&
    "option" in value &&
    "subOption" in value
  );
}

const displayLabel = computed(() => {
  if (!props.modelValue)
    return props.placeholder ?? t("common.dropDown.placeholder");

  if (isGrouped(props.modelValue)) {
    return props.subOptionValue
      ? props.modelValue.subOption[props.subOptionValue]
      : props.modelValue.subOption;
  }

  return props.modelValue[props.dataValue];
});

function handleClickOutside(event: MouseEvent) {
  if (rootEl.value && !rootEl.value.contains(event.target as Node)) {
    isOpen.value = false;
  }
}

onMounted(() => document.addEventListener("mousedown", handleClickOutside));
onBeforeUnmount(() =>
  document.removeEventListener("mousedown", handleClickOutside),
);
</script>

<template>
  <div ref="rootEl">
    <label
      v-if="title"
      class="block text-[13px] font-medium text-gray-700 mb-[3px]"
    >
      {{ title }}
    </label>

    <div class="relative">
      <div
        @click="toggleOpen"
        class="flex flex-row items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-lg cursor-pointer focus-within:border-primary focus-within:shadow-[0_0_0_3px_rgba(31,168,91,0.12)]"
      >
        <span :class="modelValue ? 'text-gray-900' : 'text-light-gray'">
          {{ displayLabel }}
        </span>
        <IconManager name="ArrowDown" class="text-light-gray" />
      </div>

      <div
        v-if="isOpen"
        class="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg border shadow-lg z-50 max-h-64 overflow-y-auto"
      >
        <ul>
          <DropdownOptionItem
            v-for="item in data"
            :key="item[dataKey]"
            :item="item"
            :dataKey="dataKey"
            :dataValue="dataValue"
            :subOptionsKey="subOptionsKey"
            :subOptionKey="subOptionKey"
            :subOptionValue="subOptionValue"
            @select="select"
          />
        </ul>
      </div>
    </div>
  </div>
</template>
